/* cali cart sync (2026-08-16)
 *
 * Keeps the cart-footer widgets honest after ANY cart mutation, including
 * app-driven ones (Bag Builder, Selleasy) that never fire Navidium's own
 * click triggers, and line removals.
 *
 * Fixes two staleness bugs reported by Ali:
 * 1. Averra (white-labeled Navidium) shipping protection: the fee quote and
 *    the in-cart protection variant were only recalculated on the app's
 *    click triggers ([name="add"], .js-qty__adjust) or on a toggle click.
 *    We re-run the app's own pipeline (nvd_init) whenever the merchandise
 *    content of the cart actually changes, then swap the in-cart protection
 *    variant to the newly computed tier when it no longer matches.
 * 2. "You're saving X in total" (drawer) and "You're saving X" (cart page):
 *    rendered once by Liquid outside Motion's ajax-refresh regions, so they
 *    never updated. Recomputed here from /cart.js on every change.
 *
 * Safety rails:
 * - Protection presence is read from the fetched cart, never from
 *   localStorage (the app never clears cart_protection, so localStorage can
 *   claim protection is in the cart when it is not). We never ADD protection
 *   that is not already in the cart; opting in stays with the user's toggle.
 * - The variant swap only runs when the widget actually rendered (proof that
 *   nvd_init completed its full path and refreshed localStorage.nvdVariant).
 * - Change detection uses a signature of the NON-protection lines only, so
 *   our own swap (which touches only the protection line) cannot re-trigger
 *   the pipeline: everything converges in one pass.
 *
 * 2026-09-09: protection add de-duplication + duplicate healing.
 * The app binds its checkout-click handler with jQuery .one() delegated to
 * '[name="checkout"], [name="checkout"] *'. jQuery builds the delegated
 * handler queue before running any handler, so one click on the text inside
 * the checkout button runs that "one-time" handler once per matching
 * ancestor of the click target (the span, then the button). When the box is
 * ticked but the protection line is not in the cart yet, each run calls
 * addProtection() = POST /cart/add.js for the same variant, a few ms apart,
 * and the order is charged 2x (3x with a deeper click target). Reproduced
 * on the live drawer 2026-09-09; 8 orders since June carried 2-3 units.
 * Two guards, independent of the app's code:
 * - the fetch net collapses a repeat add of the protection variant that
 *   starts while the first one is in flight (or within ADD_DEDUPE_MS) into
 *   the first request's response;
 * - runSync fixes a protection line whose quantity is not 1, or a second
 *   protection line, even when the merchandise signature did not change.
 */
(function () {
  'use strict';

  if (window.__caliCartSync) return;
  window.__caliCartSync = true;

  var NVD_HANDLE = 'navidium-shipping-protection';
  var DEBOUNCE_MS = 600;
  var CART_MUTATION = /\/cart\/(add|change|update|clear)/;

  var scriptEl =
    document.getElementById('CaliCartSync') || document.currentScript;
  var moneySample =
    (scriptEl && scriptEl.getAttribute('data-money-sample')) || '';

  /* ---- i18n ----
     The same sentence is rendered by Liquid in snippets/cart-drawer.liquid and
     sections/main-cart.liquid from the locale keys
     bag_builder.cart_savings_prefix / bag_builder.cart_savings_total_suffix.
     Read the SAME two values off the tag so this rewrite cannot revert a
     translated cart to English. Falls back to today's English when the
     attribute is absent or the key is unregistered. */
  function attrStr(name, fallback) {
    var v = scriptEl && scriptEl.getAttribute(name);
    if (!v || v.indexOf('Translation missing') !== -1) return fallback;
    return v;
  }
  var savingsPrefix = attrStr('data-savings-prefix', "You're saving ");
  var savingsTotalSuffix = attrStr('data-savings-total-suffix', ' in total');

  /* ---- money formatting ----
     The snippet embeds a server-rendered sample of 123456789 cents
     (1,234,567.89 units) in the visitor's presentment currency. Parsing it
     recovers prefix, suffix, thousands and decimal separators, so output
     matches Liquid's `| money` exactly for every market. */
  var moneyPattern = null;
  (function () {
    if (!moneySample) return;
    var m = moneySample.match(/1(\D*)234\D*56[78](\D*89)?/);
    if (!m) return;
    moneyPattern = {
      prefix: moneySample.slice(0, m.index),
      suffix: moneySample.slice(m.index + m[0].length),
      thousands: m[1] || '',
      decimal: m[2] ? m[2].slice(0, -2) : null
    };
  })();

  function groupDigits(intStr, sep) {
    return sep ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : intStr;
  }

  function formatCents(cents) {
    cents = Math.abs(Math.round(cents));
    var p = moneyPattern;
    if (p) {
      if (p.decimal === null) {
        return p.prefix + groupDigits(String(Math.round(cents / 100)), p.thousands) + p.suffix;
      }
      var intPart = String(Math.floor(cents / 100));
      var frac = String(cents % 100);
      if (frac.length < 2) frac = '0' + frac;
      return p.prefix + groupDigits(intPart, p.thousands) + p.decimal + frac + p.suffix;
    }
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(
          cents,
          theme.settings && theme.settings.moneyFormat
        );
      }
    } catch (err) {}
    return (cents / 100).toFixed(2);
  }

  /* ---- cart helpers ---- */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(
      function (r) {
        return r.json();
      }
    );
  }

  function isProtection(item) {
    return !!(item && item.handle && item.handle.indexOf(NVD_HANDLE) !== -1);
  }

  function merchSignature(cart) {
    var rows = [];
    (cart.items || []).forEach(function (it) {
      if (!isProtection(it)) {
        rows.push([it.variant_id, it.quantity, it.final_line_price]);
      }
    });
    return JSON.stringify([cart.currency, rows]);
  }

  var selfMutation = false;

  function cartUpdate(updates) {
    selfMutation = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(
      function (r) {
        selfMutation = false;
        return r.json();
      },
      function (err) {
        selfMutation = false;
        throw err;
      }
    );
  }

  function rebuildDrawer() {
    try {
      document.dispatchEvent(new CustomEvent('cart:build'));
    } catch (err) {}
  }

  /* ---- "You're saving X" ----
     Mirrors the Liquid in cart-drawer.liquid and main-cart.liquid:
     sum of _saved_price * quantity over lines carrying the property.
     Drawer shows it only with 2+ saved lines (a single built bag already
     has its own per-item pill); the cart page shows it whenever > 0. */
  function updateSavings(cart) {
    var cents = 0;
    var lines = 0;
    (cart.items || []).forEach(function (it) {
      if (isProtection(it)) return;
      var saved = it.properties && parseFloat(it.properties._saved_price);
      if (saved > 0) {
        cents += Math.round(saved * 100) * it.quantity;
        lines += 1;
      }
    });

    var wrappers = document.querySelectorAll('.cart__price-wrapper');
    Array.prototype.forEach.call(wrappers, function (wrap) {
      if (!wrap.querySelector('[data-subtotal]')) return;
      var inDrawer = !!(wrap.closest && wrap.closest('#CartDrawer'));
      var show = cents > 0 && (inDrawer ? lines > 1 : true);
      var el = wrap.querySelector('.cart__savings-text');
      if (!show) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
        return;
      }
      if (!el) {
        el = document.createElement('div');
        el.className = 'cart__savings-text';
        wrap.appendChild(el);
      }
      var text =
        savingsPrefix + formatCents(cents) + (inDrawer ? savingsTotalSuffix : '');
      if (el.textContent !== text) el.textContent = text;
    });
  }

  /* ---- Navidium re-quote + variant swap ---- */
  function canInit() {
    try {
      return typeof nvd_init === 'function';
    } catch (err) {
      return false;
    }
  }

  function widgetRendered() {
    var el = document.querySelector('.nvd-mini');
    return !!(el && el.innerHTML && el.innerHTML.length > 0);
  }

  function syncProtection(nvdLines) {
    if (!nvdLines.length) return Promise.resolve();
    if (!widgetRendered()) return Promise.resolve();

    var correct = null;
    try {
      correct = localStorage.getItem('nvdVariant');
    } catch (err) {}
    if (!correct) return Promise.resolve();

    var wrong =
      nvdLines.length > 1 ||
      nvdLines[0].quantity !== 1 ||
      String(nvdLines[0].variant_id) !== String(correct);
    if (!wrong) return Promise.resolve();

    if (nvdLines.length === 1) {
      /* One line: a single absolute write. Same variant with the wrong
         quantity collapses to {correct: 1}; a stale tier is {old: 0, new: 1}. */
      var updates = {};
      updates[nvdLines[0].variant_id] = 0;
      updates[correct] = 1;
      return cartUpdate(updates).then(rebuildDrawer);
    }

    /* Several protection lines (same variant with different properties):
       variant-id keys cannot address them and mixing line keys with a
       variant id in one call removes the variant altogether (verified
       2026-09-09). Zero every line by its key, then add the correct tier. */
    var zero = {};
    nvdLines.forEach(function (it) {
      zero[it.key || it.variant_id] = 0;
    });
    return cartUpdate(zero)
      .then(function () {
        var add = {};
        add[correct] = 1;
        return cartUpdate(add);
      })
      .then(rebuildDrawer);
  }

  /* ---- protection add de-duplication ----
     The app adds protection with POST /cart/add.js {id, quantity: 1,
     properties} where id is the variant it just stored in
     localStorage.nvdVariant. Two such requests for that variant within
     ADD_DEDUPE_MS are one customer intent, never two units. */
  var ADD_DEDUPE_MS = 1500;
  var protectionAdd = null; /* { id, promise, t } */

  function isProtectionVariantId(id) {
    try {
      var a = localStorage.getItem('nvdVariant');
      var b = localStorage.getItem('cart_protection');
      return (!!a && String(a) === id) || (!!b && String(b) === id);
    } catch (err) {
      return false;
    }
  }

  function singleAddVariantId(init) {
    var body = init && init.body;
    if (typeof body !== 'string' || body.charAt(0) !== '{') return null;
    try {
      var j = JSON.parse(body);
      if (j && j.id !== undefined && j.id !== null && !j.items) {
        return String(j.id);
      }
    } catch (err) {}
    return null;
  }

  function cloneResponse(r) {
    try {
      return r.clone();
    } catch (err) {
      return r;
    }
  }

  /* ---- pipeline ---- */
  var lastSig = null;
  var syncTimer = null;

  function schedule() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(runSync, DEBOUNCE_MS);
  }

  function runSync() {
    syncTimer = null;
    getCart()
      .then(function (cart) {
        if (!cart || !cart.items) return;
        updateSavings(cart);

        var nvdLines = cart.items.filter(isProtection);
        var merchCount = cart.items.length - nvdLines.length;

        var sig = merchSignature(cart);
        if (sig === lastSig) {
          /* Merchandise unchanged, but a doubled or duplicated protection
             line still needs fixing (the app's own dedupe sets it to 0 and
             re-adds at checkout, which is where the doubling comes from). */
          var nvdDuplicated =
            nvdLines.length > 1 ||
            (nvdLines.length === 1 && nvdLines[0].quantity !== 1);
          if (nvdDuplicated && merchCount > 0) {
            syncProtection(nvdLines).catch(function () {});
          }
          return;
        }
        lastSig = sig;

        /* All merchandise gone: a protection-only cart is junk. Clear it so
           the drawer shows its real empty state instead of a lone hidden
           line with a fee-only checkout total. */
        if (merchCount === 0) {
          if (nvdLines.length) {
            var updates = {};
            nvdLines.forEach(function (it) {
              updates[it.variant_id] = 0;
            });
            cartUpdate(updates).then(rebuildDrawer, function () {});
          }
          return;
        }

        if (!canInit()) return;

        /* nvd_init: the app's own recalculation. Reads the fresh cart,
           excludes the protection line, computes the correct tier, rebuilds
           the widget UI and refreshes localStorage.nvdVariant. */
        Promise.resolve()
          .then(function () {
            return nvd_init();
          })
          .then(function () {
            return syncProtection(nvdLines);
          })
          .catch(function () {});
      })
      .catch(function () {});
  }

  /* ---- triggers ----
     Motion's own events plus a fetch/XHR net for app-driven mutations
     (same pattern as cali-shipping-bar.js; both patches chain safely). */
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('ajaxProduct:added', schedule);
  document.addEventListener('cart:open', schedule);

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';

      if (/\/cart\/add\.js/.test(url)) {
        var vid = singleAddVariantId(init);
        if (vid && isProtectionVariantId(vid)) {
          var now = Date.now();
          if (
            protectionAdd &&
            protectionAdd.id === vid &&
            now - protectionAdd.t < ADD_DEDUPE_MS
          ) {
            /* Duplicate: no second request. The caller still gets a 200
               with the cart line, so its own bookkeeping and redirect run. */
            return protectionAdd.promise.then(cloneResponse);
          }
          var first = origFetch.apply(this, arguments);
          protectionAdd = { id: vid, promise: first, t: now };
          first.then(schedule, function () {});
          return first.then(cloneResponse);
        }
      }

      var promise = origFetch.apply(this, arguments);
      if (CART_MUTATION.test(url) && !selfMutation) {
        promise.then(schedule, function () {});
      }
      return promise;
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (url && CART_MUTATION.test(String(url)) && !selfMutation) {
      this.addEventListener('load', schedule);
    }
    return origOpen.apply(this, arguments);
  };

  /* Initial pass, delayed so Navidium's own load-time init lands first.
     Heals carts persisted from a previous session whose protection tier no
     longer matches their contents. */
  setTimeout(runSync, 1200);
})();

